/**
 * 新廠商申請與正式廠商主檔共用的業務資料契約。
 *
 * API 及資料庫仍可暫時保留舊欄位以支援既有資料，但新功能只應依這組
 * profile 欄位讀寫，避免申請與主檔再次分叉。
 */

export const VENDOR_PRODUCT_CATEGORY_CODES = [
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
  'I',
  'J',
  'K',
  'L',
  'M',
  'N',
  'P',
] as const;

export type VendorProductCategoryCode = (typeof VENDOR_PRODUCT_CATEGORY_CODES)[number];

export const VENDOR_PRODUCT_CATEGORY_LABELS: Record<VendorProductCategoryCode, string> = {
  A: 'A｜處方箋',
  B: 'B｜OTC用藥',
  C: 'C｜甲類成藥（西藥）',
  D: 'D｜乙類成藥（西藥）',
  E: 'E｜甲類成藥（中藥）',
  F: 'F｜處方藥（中藥）',
  G: 'G｜乙類成藥（中藥）',
  H: 'H｜奶粉／液體營養配方',
  I: 'I｜尿布',
  J: 'J｜醫療器材',
  K: 'K｜美粧品（粧字碼）',
  L: 'L｜一般用品',
  M: 'M｜健康食品',
  N: 'N｜環境用藥',
  P: 'P｜惠市集',
};

export const VENDOR_PAYMENT_TERMS = ['PREPAID', 'WEEKLY', 'CASH_ON_DELIVERY', 'MONTHLY'] as const;
export type VendorPaymentTerm = (typeof VENDOR_PAYMENT_TERMS)[number];

export const VENDOR_PAYMENT_METHODS = ['CASH', 'CHECK', 'BANK_TRANSFER', 'OTHER'] as const;
export type VendorPaymentMethod = (typeof VENDOR_PAYMENT_METHODS)[number];

export const VENDOR_DELIVERY_SCOPES = ['WAREHOUSE', 'STORE', 'BOTH'] as const;
export type VendorDeliveryScope = (typeof VENDOR_DELIVERY_SCOPES)[number];

export const VENDOR_DELIVERY_CYCLES = [
  'DAILY',
  'WEEKLY',
  'FIXED_WEEKDAYS',
  'BIWEEKLY',
  'MONTHLY',
  'ORDER_NOTICE',
  'OTHER',
] as const;
export type VendorDeliveryCycle = (typeof VENDOR_DELIVERY_CYCLES)[number];

export const VENDOR_WEEKDAYS = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
] as const;
export type VendorWeekday = (typeof VENDOR_WEEKDAYS)[number];

export const VENDOR_FREIGHT_RULES = [
  'FREE',
  'THRESHOLD',
  'FIXED',
  'ACTUAL',
  'VENDOR_PAYS',
  'COMPANY_PAYS',
  'OTHER',
] as const;
export type VendorFreightRule = (typeof VENDOR_FREIGHT_RULES)[number];

export const VENDOR_RETURN_HANDLING_METHODS = [
  'RETURN',
  'EXCHANGE',
  'RETURN_OR_EXCHANGE',
  'NONE',
] as const;
export type VendorReturnHandlingMethod = (typeof VENDOR_RETURN_HANDLING_METHODS)[number];

export const VENDOR_RETURN_CONDITION_TYPES = [
  'NEAR_EXPIRY',
  'DEFECT',
  'INTOLERANCE',
  'OTHER',
] as const;
export type VendorReturnConditionType = (typeof VENDOR_RETURN_CONDITION_TYPES)[number];

export const VENDOR_RETURN_FREIGHT_RESPONSIBILITIES = [
  'VENDOR',
  'COMPANY',
  'BY_REASON',
  'AGREEMENT',
  'OTHER',
] as const;
export type VendorReturnFreightResponsibility =
  (typeof VENDOR_RETURN_FREIGHT_RESPONSIBILITIES)[number];

export const VENDOR_INVOICE_FORMATS = [
  'ELECTRONIC',
  'PAPER_2',
  'PAPER_3',
  'E_CALCULATOR',
  'CASH_REGISTER',
  'EXEMPT',
  'OTHER',
] as const;
export type VendorInvoiceFormat = (typeof VENDOR_INVOICE_FORMATS)[number];

import type { SupplierQualification, SupplierDocumentView } from './supplier-assessment';

export const VENDOR_INVOICE_DELIVERY_METHODS = ['WITH_GOODS', 'MAIL', 'OTHER'] as const;
export type VendorInvoiceDeliveryMethod = (typeof VENDOR_INVOICE_DELIVERY_METHODS)[number];

export interface VendorBusinessProfile {
  company_name: string;
  company_short_name: string | null;
  tax_id: string;
  phone: string;
  responsible_person?: string | null;
  address: string;
  company_email: string;
  website: string;
  company_fax: string;
  company_intro: string;
  notes: string;
  principal_brands: string[];
  supplied_product_categories: VendorProductCategoryCode[];
}

export interface VendorContactProfile {
  contact_name: string;
  contact_job_title: string | null;
  contact_phone: string;
  contact_email: string;
  contact_line?: string | null;
  vendor_closing_day: string;
  daily_order_cutoff_time: string;
}

export interface VendorPaymentTerms {
  payment_term: VendorPaymentTerm | '';
  monthly_payment_days: number | null;
  payment_method: VendorPaymentMethod | '';
  payment_method_other: string;
  weekly_payment_note: string;
  payment_rule_acknowledged: boolean;
}

export interface VendorDeliveryTerms {
  delivery_scope: VendorDeliveryScope | '';
  delivery_cycle: VendorDeliveryCycle | '';
  delivery_weekdays: VendorWeekday[];
  delivery_cycle_other: string;
  vendor_closing_day: string;
  daily_order_cutoff_time: string;
  minimum_order_amount: number | null;
  minimum_order_quantity: number | null;
  freight_rule: VendorFreightRule | '';
  freight_threshold_amount: number | null;
  freight_fixed_amount: number | null;
  freight_other_note: string;
  transport_responsibility?: string | null;
  transport_company_name?: string | null;
  transport_temperature_conditions?: string[];
  transport_temperature_other?: string | null;
  transport_methods?: string[];
  transport_notes?: string | null;
  transportation_responsibility?: string | null;
  logistics_providers?: string | null;
  temperature_control_options?: string[];
  delivery_methods?: string[];
  transportation_remarks?: string | null;
}

export interface VendorReturnTerms {
  return_scope: VendorDeliveryScope | '';
  return_handling_method: VendorReturnHandlingMethod | '';
  return_condition_types: VendorReturnConditionType[];
  return_near_expiry_condition: string;
  return_defect_condition: string;
  return_intolerance_condition: string;
  return_other_condition: string;
  return_condition_description: string;
  return_freight_responsibility: VendorReturnFreightResponsibility | '';
  return_freight_rule_note: string;
}

export interface VendorInvoiceTerms {
  invoice_format: VendorInvoiceFormat | '';
  invoice_format_other: string;
  invoice_delivery_method: VendorInvoiceDeliveryMethod | '';
  invoice_delivery_other: string;
  invoice_same_as_primary: boolean;
  invoice_contact_name: string;
  invoice_contact_phone: string;
  invoice_email: string;
  reconciliation_same_as_primary: boolean;
  reconciliation_contact_name: string;
  reconciliation_contact_phone: string;
  reconciliation_email: string;
  reconciliation_terms_acknowledged: boolean;
}

export interface VendorBankDetails {
  id?: string;
  bank_name: string;
  bank_code: string;
  branch_name: string;
  branch_code: string;
  bank_account_name: string;
  bank_account_number?: string;
  bank_account_number_masked?: string;
}

export interface VendorInternalManagement {
  vendor_code: string;
  responsible_buyer_id: string | null;
  vendor_status: string;
  active_date: string | null;
  internal_notes: string;
  credit_trade_notes: string;
  risk_flag: string;
  last_reviewed_by: string | null;
  last_reviewed_at: string | null;
}


export interface VendorLicenseItem {
  type: string;
  type_name_zh: string;
  license_number: string | null;
  issue_date: string | null;
  expiry_date: string | null;
  status: 'VALID' | 'EXPIRING_SOON' | 'EXPIRED' | 'NOT_PROVIDED' | 'NOT_APPLICABLE' | string;
  document?: SupplierDocumentView | null;
  notes?: string | null;
}

export interface VendorProfile {
  business: VendorBusinessProfile;
  contact: VendorContactProfile;
  payment_terms: VendorPaymentTerms;
  delivery_terms: VendorDeliveryTerms;
  return_terms: VendorReturnTerms;
  invoice_terms: VendorInvoiceTerms;
  bank_details: VendorBankDetails[];
  qualification?: SupplierQualification;
}

